import { Component } from 'react'
import { toast } from 'react-toastify'
import { IClientUID } from '../../../api/clients'
import { getClientCustomerData, updateClientCustomerData, ICustomerData, CustomerDataType } from '../../../api/0x0001/customerdata'
import { getString } from '../../../locale'
import Loader from '../../../components/loader'
import classnames from 'classnames'
import { saveMachine } from '../../../api/0x0001/machine'

type Props = {
    clientId: IClientUID
}

type State = {
    settings?: Record<keyof ICustomerData, string>
    updates: Partial<ICustomerData>
    errors: Partial<Record<keyof ICustomerData, string>>
    unsaved: boolean
}

class CustomerDataTab extends Component<Props, State> {
    state: State = {
        settings: undefined,
        updates: {},
        errors: {},
        unsaved: false,
    }

    componentDidMount = () => {
        this.loadSettings()
    }

    loadSettings = async () => {
        try {
            const settings = (await getClientCustomerData(this.props.clientId)).data
            console.log('settings', settings)
            this.setState({ settings, updates: {}, errors: {}, unsaved: false })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async saveSettings() {
        try {
            const { updates, errors } = this.state
            if (Object.keys(updates).length === 0) {
                await saveMachine(this.props.clientId, this.state.settings!)
                return toast.error(getString('settings.no_updates'))
            }
            if (Object.keys(errors).length > 0) {
                return toast.error(getString('settings.fix_errors'))
            }
            await updateClientCustomerData(
                this.props.clientId,
                Object.entries(updates).map(([key, value]) => ({
                    type: CustomerDataType[key as any] as any,
                    value: value!,
                })),
            )
            await this.loadSettings()

            // Save the data in our database
            await saveMachine(this.props.clientId, this.state.settings!)

            toast.success(getString('common.success', 'common.saved'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    handleNumberChange(name: keyof ICustomerData, value: string) {
        const { settings, updates, errors } = this.state
        settings![name] = value
        updates[name] = value
        // delete errors[name]
        this.setState({ settings, updates, unsaved: true })
    }

    render() {
        if (this.state.settings === undefined) return <Loader size={14} />
        return (
            <div>
                {this.renderActions()}
                {this.renderCustomerData()}
            </div>
        )
    }

    renderActions() {
        return (
            <div className="grid grid-cols-4 gap-5">
                {this.renderActionButton(getString('common.save'), this.saveSettings.bind(this))}
                {this.renderActionButton(getString('settings.refresh'), this.loadSettings.bind(this))}
                {this.renderUnsavedChanges()}
            </div>
        )
    }

    renderActionButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) {
        return (
            <button
                onClick={onClick}
                className="group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-501"
            >
                {label}
            </button>
        )
    }

    renderUnsavedChanges() {
        if (!this.state.unsaved) return undefined
        return <div className="dark:text-white flex items-center">* {getString('common.unsaved_changes')}</div>
    }

    renderInputErrorMessage(message: string) {
        return <div className="text-red-500 text-sm">{message}</div>
    }

    renderCustomerData() {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCustomerName()}
                {this.renderAddress()}
                {this.renderCity()}
                {this.renderTelephone()}
                {this.renderCellphone1()}
                {this.renderCellphone2()}
                {this.renderMail()}
                {this.renderCompany()}
                {this.renderNotes()}
            </div>
        )
    }

    renderCustomerName() {
        const { settings, errors } = this.state
        const error = errors.name !== undefined
        return (
            <div className="">
                <label className="dark:text-white text-sm">{getString('customerdata.name')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.name}
                    onInput={(e: any) => this.handleNumberChange('name', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.name!)}
            </div>
        )
    }

    renderAddress() {
        const { settings, errors } = this.state
        const error = errors.address !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.address')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.address}
                    onInput={(e: any) => this.handleNumberChange('address', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.address!)}
            </div>
        )
    }

    renderCity() {
        const { settings, errors } = this.state
        const error = errors.city !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.city')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.city}
                    onInput={(e: any) => this.handleNumberChange('city', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.city!)}
            </div>
        )
    }

    renderTelephone() {
        const { settings, errors } = this.state
        const error = errors.telephone !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.telephone')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.telephone}
                    onInput={(e: any) => this.handleNumberChange('telephone', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.telephone!)}
            </div>
        )
    }

    renderCellphone1() {
        const { settings, errors } = this.state
        const error = errors.cellphone1 !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.cellphone1')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.cellphone1}
                    onInput={(e: any) => this.handleNumberChange('cellphone1', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.cellphone1!)}
            </div>
        )
    }

    renderCellphone2() {
        const { settings, errors } = this.state
        const error = errors.cellphone2 !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.cellphone2')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.cellphone2}
                    onInput={(e: any) => this.handleNumberChange('cellphone2', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.cellphone2!)}
            </div>
        )
    }

    renderMail() {
        const { settings, errors } = this.state
        const error = errors.mail !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.mail')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.mail}
                    onInput={(e: any) => this.handleNumberChange('mail', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.mail!)}
            </div>
        )
    }

    renderCompany() {
        const { settings, errors } = this.state
        const error = errors.company !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.company')}</label>
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.company}
                    onInput={(e: any) => this.handleNumberChange('company', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.company!)}
            </div>
        )
    }

    renderNotes() {
        const { settings, errors } = this.state
        const error = errors.notes !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('customerdata.notes')}</label>
                <textarea
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.notes}
                    onInput={(e: any) => this.handleNumberChange('notes', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.notes!)}
            </div>
        )
    }
}

export default CustomerDataTab
